import type { SiteLang } from "@/hooks/useLanguage";

type BoardI18n = {
  seo: { title: string; description: string };
  breadcrumb: { about: string; board: string };
  hero: {
    kicker: string;
    titlePre: string;
    titleAccent: string;
    subtitle: string;
    ctaAdvisors: string;
    ctaValue: string;
  };
  value: {
    kicker: string;
    title: string;
    subtitle: string;
    items: { title: string; desc: string }[];
  };
  support: {
    kicker: string;
    title: string;
    p1: string;
    p2: string;
    bullets: string[];
  };
  profiles: {
    kicker: string;
    title: string;
    subtitle: string;
    advisorRole: string;
  };
};

const en: BoardI18n = {
  seo: {
    title: "Board of Advisors — QbD Group",
    description: "Meet QbD Group's Board of Advisors — esteemed industry leaders providing strategic counsel to strengthen our position in the life sciences industry.",
  },
  breadcrumb: { about: "About us", board: "Board of advisors" },
  hero: {
    kicker: "Long-term strategic counsel",
    titlePre: "Meet our",
    titleAccent: "board of advisors",
    subtitle: "At QbD Group, we believe that continuous growth and innovation require the right expertise and strategic insights. Our Board of Advisors is an esteemed group of industry leaders who help guide our long-term strategy.",
    ctaAdvisors: "Meet the advisors",
    ctaValue: "Why an advisory board?",
  },
  value: {
    kicker: "Global industry experience",
    title: "Why is this initiative valuable?",
    subtitle: "In today's fast-changing world, staying competitive requires more than operational excellence — it demands a visionary approach. Our Board of Advisors brings:",
    items: [
      { title: "Global industry expertise", desc: "Helping QbD Group scale and innovate with deep sector knowledge." },
      { title: "Independent insights", desc: "Challenging and refining our strategies with an objective perspective." },
      { title: "Extensive network", desc: "Facilitating collaborations and partnerships across the life sciences industry." },
      { title: "Proven leadership", desc: "Guiding companies through growth, transformation, and long-term success." },
    ],
  },
  support: {
    kicker: "Growth and acquisitions",
    title: "How the board supports QbD Group",
    p1: "The Board of Advisors has been formed to offer <strong class=\"text-foreground\">strategic counsel to QbD Group's leadership</strong>, helping us navigate the evolving life sciences landscape.",
    p2: "By leveraging their expertise in international business growth, mergers & acquisitions, scientific advancements, and industry best practices, they provide an invaluable external perspective.",
    bullets: [
      "Strategic guidance rather than operational oversight.",
      "An external perspective that complements our internal expertise.",
      "Industry benchmarking and insights to refine our direction.",
    ],
  },
  profiles: {
    kicker: "The board",
    title: "Who are they?",
    subtitle: "The Board of Advisors is composed of distinguished professionals, each bringing their unique experiences and perspectives to QbD Group.",
    advisorRole: "Board Advisor",
  },
};

const es: BoardI18n = {
  seo: {
    title: "Consejo asesor — QbD Group",
    description: "Conoce al consejo asesor de QbD Group — líderes reconocidos de la industria que proporcionan asesoramiento estratégico para fortalecer nuestra posición en Life Sciences.",
  },
  breadcrumb: { about: "Sobre nosotros", board: "Consejo asesor" },
  hero: {
    kicker: "Orientación a largo plazo",
    titlePre: "Conoce a nuestro",
    titleAccent: "consejo asesor",
    subtitle: "En QbD Group, creemos que el crecimiento continuo y la innovación requieren la experiencia y las perspectivas estratégicas adecuadas. Nuestro consejo asesor es un grupo distinguido de líderes del sector que ayudan a guiar nuestra estrategia a largo plazo.",
    ctaAdvisors: "Conoce a los asesores",
    ctaValue: "¿Por qué un consejo asesor?",
  },
  value: {
    kicker: "Experiencia sectorial global",
    title: "¿Por qué es valiosa esta iniciativa?",
    subtitle: "En un mundo que cambia rápidamente, mantenerse competitivo requiere más que excelencia operativa — exige un enfoque visionario. Nuestro consejo asesor aporta:",
    items: [
      { title: "Experiencia global en la industria", desc: "Ayudando a QbD Group a escalar e innovar con un conocimiento profundo del sector." },
      { title: "Perspectivas independientes", desc: "Desafiando y refinando nuestras estrategias con una perspectiva objetiva." },
      { title: "Red de contactos extensa", desc: "Facilitando colaboraciones y alianzas en toda la industria Life Sciences." },
      { title: "Liderazgo probado", desc: "Guiando a empresas a través del crecimiento, la transformación y el éxito a largo plazo." },
    ],
  },
  support: {
    kicker: "Crecimiento y adquisiciones",
    title: "Cómo el consejo apoya a QbD Group",
    p1: "El consejo asesor se ha formado para ofrecer <strong class=\"text-foreground\">asesoramiento estratégico al liderazgo de QbD Group</strong>, ayudándonos a navegar el cambiante panorama Life Sciences.",
    p2: "Aprovechando su experiencia en crecimiento empresarial internacional, fusiones y adquisiciones, avances científicos y mejores prácticas del sector, proporcionan una perspectiva externa invaluable.",
    bullets: [
      "Orientación estratégica en lugar de supervisión operativa.",
      "Una perspectiva externa que complementa nuestra experiencia interna.",
      "Benchmarking sectorial y perspectivas para afinar nuestra dirección.",
    ],
  },
  profiles: {
    kicker: "El consejo",
    title: "¿Quiénes son?",
    subtitle: "El consejo asesor está compuesto por profesionales distinguidos, cada uno aportando sus experiencias únicas y perspectivas a QbD Group.",
    advisorRole: "Consejero asesor",
  },
};

const zh: BoardI18n = {
  seo: {
    title: "顾问委员会 — QbD Group",
    description: "认识 QbD Group 的顾问委员会 — 受人尊敬的行业领袖,他们提供战略建议,助力我们在生命科学行业中巩固地位。",
  },
  breadcrumb: { about: "关于我们", board: "顾问委员会" },
  hero: {
    kicker: "独立视角长期战略",
    titlePre: "认识我们的",
    titleAccent: "顾问委员会",
    subtitle: "在 QbD Group,我们相信持续的增长与创新需要正确的专业知识与战略洞察。我们的顾问委员会由备受尊敬的行业领袖组成,帮助引导我们的长期战略。",
    ctaAdvisors: "认识顾问",
    ctaValue: "为什么需要顾问委员会?",
  },
  value: {
    kicker: "全球经验独立洞察",
    title: "为什么这项举措如此宝贵?",
    subtitle: "在瞬息万变的当今世界,保持竞争力不仅需要卓越的运营能力,更需要富有远见的方法。我们的顾问委员会带来:",
    items: [
      { title: "全球行业专长", desc: "凭借深厚的行业知识帮助 QbD Group 扩展规模与持续创新。" },
      { title: "独立洞察", desc: "以客观视角挑战并完善我们的战略。" },
      { title: "广泛的人脉网络", desc: "促进生命科学行业内的合作与伙伴关系。" },
      { title: "经验证的领导力", desc: "引导企业实现增长、转型与长期成功。" },
    ],
  },
  support: {
    kicker: "国际增长并购洞察",
    title: "委员会如何支持 QbD Group",
    p1: "顾问委员会的成立旨在为 <strong class=\"text-foreground\">QbD Group 的领导层提供战略建议</strong>,帮助我们驾驭不断变化的生命科学领域。",
    p2: "他们在国际业务增长、并购、科学进步以及行业最佳实践方面的专业知识,为我们提供了宝贵的外部视角。",
    bullets: [
      "提供战略指导,而非运营监督。",
      "外部视角,补充我们的内部专长。",
      "行业基准与洞察,帮助我们完善方向。",
    ],
  },
  profiles: {
    kicker: "委员会",
    title: "他们是谁?",
    subtitle: "顾问委员会由杰出的专业人士组成,每位成员都为 QbD Group 带来独特的经验与视角。",
    advisorRole: "委员会顾问",
  },
};

const translations: Record<SiteLang, BoardI18n> = { en, es, "zh-cn": zh };
export const getBoardT = (lang: SiteLang) => translations[lang] ?? en;
